import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { v4 as uuid } from 'uuid'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { useCheckout } from '@/composables/useCheckout'
import { useKitchenOrders } from '@/composables/useKitchenOrders'
import { useConfigStore } from '@/store/config'
import { useTabsStore } from '@/store/tabs'
import sync from '@/sync/service'
import type { Tab } from '@/types'

export const useFastMode = defineStore('fastMode', () => {
  const router = useRouter()

  const configStore = useConfigStore()
  const { config } = storeToRefs(configStore)

  const tabsStore = useTabsStore()
  const { tabs } = storeToRefs(tabsStore)

  const tabId = ref<string>(uuid())
  const pickupType = ref<boolean>(false)

  const virtualTab = computed((): Tab => {
    let newTab = tabsStore.formatTab({
      tab: {
        id: tabId.value,
        pickupType: pickupType.value ? 'takeAway' : undefined
      }
    })

    newTab = {
      ...newTab,
      shared: [],
      seats: [],
      bills: [],
      kitchenOrders: []
    }

    return newTab
  })

  function newTab() {
    tabId.value = uuid()
  }

  function addTabToSystem() {
    const currentFastTab = tabs.value[tabId.value]
    if (currentFastTab) return

    newTab()

    const tab = tabsStore.formatTab({
      tab: {
        id: tabId.value,
        pickupType: pickupType.value ? 'takeAway' : undefined
      }
    })

    sync.record('tabOpened', tab)

    return tabId.value
  }

  function saveTab(): void {
    if (!tabId.value) return

    addTabToSystem()

    newTab()
  }

  async function doFastPayment(method: string) {
    const localTabId = tabId.value
    if (!tabs.value[localTabId]) return

    if (config.value.enableKitchenOrders) {
      const { sendKitchenOrder } = useKitchenOrders(localTabId)
      await sendKitchenOrder('all')
    }

    const { charge, paymentMethod, paymentMethods } = useCheckout(localTabId)

    const selectedMethod = paymentMethods.value.find(pm => pm.type === method)
    if (selectedMethod) {
      paymentMethod.value = selectedMethod
    }

    await charge()

    closeTab()
  }

  async function doCheckout(): Promise<void> {
    if (!tabs.value[tabId.value]) return

    if (config.value.enableKitchenOrders) {
      const { sendKitchenOrder } = useKitchenOrders(tabId.value)
      await sendKitchenOrder('all')
    }

    router.push({
      name: 'checkout',
      params: {
        tabId: tabId.value
      },
      query: { referrer: 'fastMode' }
    })
  }

  function closeTab() {
    tabsStore.closeTab({ tabId: tabId.value, closedWithPin: false })
    newTab()
  }

  return {
    tabId,
    pickupType,
    virtualTab,

    closeTab,
    doFastPayment,
    doCheckout,
    addTabToSystem,
    newTab,
    saveTab
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFastMode, import.meta.hot))
}
